import { FormikProps } from 'formik';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { PasswordRequirements } from '@components/molecules/PasswordRequirements';
import { Card } from '@components/atoms/Card';
import { useTranslation } from '@hooks/useTranslation';

export interface ResetPasswordFormProps {
  onBack?: () => void;
  formik: FormikProps<{
    email: string;
    confirmationCode: string;
    newPassword: string;
    confirmPassword: string;
  }>;
  submitting?: boolean;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  formik,
  onBack,
  submitting = false,
}) => {
  const tLogin = useTranslation('login');
  return (
    <div className="flex justify-center items-center h-screen">
      <Card className="w-full max-w-md mx-auto">
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {tLogin.resetPasswordTitle}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {tLogin.resetPasswordSubtitle}
          </p>
        </div>
        <form
          onSubmit={formik.handleSubmit}
          autoComplete="off"
          className="flex flex-col gap-4 mt-8">
          <FormGroup
            error={formik.errors.email}
            label={tLogin.email}
            htmlFor="reset-email">
            <Input
              id="reset-email"
              name="email"
              type="email"
              autoComplete="email"
              onBlur={formik.handleBlur}
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder={tLogin.enterEmail}
              error={Boolean(formik.touched.email && formik.errors.email)}
            />
          </FormGroup>
          <FormGroup htmlFor="reset-code">
            <Input
              id="reset-code"
              name="confirmationCode"
              autoComplete="one-time-code"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.confirmationCode}
              placeholder={tLogin.enterResetCode}
              error={Boolean(
                formik.touched.confirmationCode &&
                  formik.errors.confirmationCode,
              )}
            />
          </FormGroup>
          <FormGroup label={tLogin.newPasswordLabel} htmlFor="reset-password">
            <Input
              name="newPassword"
              type="password"
              id="reset-password"
              autoComplete="new-password"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.newPassword}
              placeholder={tLogin.enterPassword}
              error={Boolean(
                formik.touched.newPassword && formik.errors.newPassword,
              )}
            />
          </FormGroup>
          <FormGroup
            error={formik.errors.confirmPassword}
            label={tLogin.confirmPasswordLabel}
            htmlFor="reset-confirm-password">
            <Input
              type="password"
              name="confirmPassword"
              onBlur={formik.handleBlur}
              id="reset-confirm-password"
              autoComplete="new-password"
              onChange={formik.handleChange}
              placeholder={tLogin.confirmNewPasswordPlaceholder}
              value={formik.values.confirmPassword}
              error={Boolean(
                formik.touched.confirmPassword && formik.errors.confirmPassword,
              )}
            />
          </FormGroup>
          <PasswordRequirements value={formik.values.newPassword} />
          <div className="flex gap-2 mt-4">
            {onBack && (
              <Button type="button" variant="secondary" onClick={onBack}>
                {tLogin.back}
              </Button>
            )}
            <Button
              fullWidth
              type="submit"
              variant="primary"
              disabled={submitting}>
              {tLogin.resetPasswordSubmit}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ResetPasswordForm;
